import { useMemo, useState } from 'react';
import { Button, Card, Icon, IconButton, Switch, Tabs, Tag } from '../ds';
import { EmptyState, Eyebrow, Mono, MutedHeading, Screen, ScreenTitle } from '../components/primitives';
import { GrowthChart, summarize } from '../components/GrowthChart';
import { Sheet, SheetRow } from '../components/Sheet';
import { Field, TextInput } from './PrepScreen';
import { useToast } from '../components/Toast';
import * as api from '../lib/db';
import { useFamily } from '../state/FamilyContext';
import { estimatePercentileLabel } from '../data/whoReference';
import { growthKindLabel } from '../lib/labels';
import { MILESTONE_SUGGESTIONS } from '../data/defaults';
import { ageInMonths, ageLabel, formatMonthDay, parseISODate, toISODate } from '../lib/time';
import type { GrowthKind, Milestone } from '../lib/types';

/**
 * せいちょう / Growth.
 *
 * Weight and height plotted against the WHO percentile band — the "so the
 * grandparents can follow along" part — plus a low-key Milestones section that
 * can be switched off, since one parent already keeps those by hand.
 */

export function GrowthScreen() {
  const { familyId, family, me, children, growth, milestones } = useFamily();
  const toast = useToast();

  const [kind, setKind] = useState<GrowthKind>('weight');
  const [adding, setAdding] = useState(false);
  const [addingMilestone, setAddingMilestone] = useState(false);

  const babies = useMemo(
    () => (children.length ? children.filter((c) => c.kind === 'baby').concat(children.filter((c) => c.kind !== 'baby')) : []),
    [children],
  );
  const [childId, setChildId] = useState('');
  const child = babies.find((c) => c.id === childId) ?? babies[0] ?? null;

  const forChild = useMemo(
    () => growth.filter((g) => child && g.childId === child.id),
    [growth, child],
  );
  const ofKind = useMemo(() => forChild.filter((g) => g.kind === kind), [forChild, kind]);

  const { unit, latestLabel } = growthKindLabel(kind);
  const stats = useMemo(
    () => (child ? summarize(kind, child.sex, child.birthDate, ofKind) : null),
    [kind, child, ofKind],
  );

  const childMilestones = useMemo(
    () => milestones.filter((m) => child && m.childId === child.id),
    [milestones, child],
  );

  const showMilestones = family?.showMilestones !== false;

  if (!child) {
    return (
      <Screen>
        <ScreenTitle eyebrow="Growth" title="成長きろく" />
        <Card padding="md">
          <EmptyState icon={<Icon name="TrendingUp" size={24} />}>
            お子さんが登録されていません。設定から追加できます。
          </EmptyState>
        </Card>
      </Screen>
    );
  }

  const percentile =
    stats?.latest != null
      ? estimatePercentileLabel(
          kind,
          child.sex,
          ageInMonths(child.birthDate, stats.latest.measuredAt),
          stats.latest.value,
        )
      : null;

  return (
    <Screen>
      <ScreenTitle
        eyebrow={`Growth · ${child.name}`}
        title="成長きろく"
        subtitle={`${ageLabel(child.birthDate)} · ${child.nameEn || child.name}`}
      />

      {babies.length > 1 ? (
        <div style={{ marginBottom: 12 }}>
          <Tabs
            variant="pill"
            value={child.id}
            onChange={setChildId}
            items={babies.map((c) => ({ value: c.id, label: c.name }))}
          />
        </div>
      ) : null}

      <div style={{ marginBottom: 16 }}>
        <Tabs
          variant="pill"
          value={kind}
          onChange={(v) => setKind(v as GrowthKind)}
          items={[
            { value: 'weight', label: '体重 Weight' },
            { value: 'height', label: '身長 Height' },
          ]}
        />
      </div>

      {/* ---- latest ---- */}
      <Card padding="md" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
          <div style={{ minWidth: 0 }}>
            <Eyebrow>{latestLabel}</Eyebrow>
            {stats?.latest ? (
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: 'var(--text-4xl)',
                  lineHeight: 1,
                  marginTop: 4,
                }}
              >
                {stats.latest.value}
                <span style={{ fontSize: 'var(--text-lg)', color: 'var(--text-subtle)', marginLeft: 4 }}>
                  {unit}
                </span>
              </div>
            ) : (
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-subtle)', marginTop: 8 }}>
                まだ記録がありません
              </div>
            )}
          </div>

          {stats?.latest ? (
            <div style={{ textAlign: 'right', flex: 'none' }}>
              {stats.delta != null ? (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                    fontSize: 'var(--text-sm)',
                    fontWeight: 700,
                    color: stats.delta >= 0 ? 'var(--sage-600)' : 'var(--text-muted)',
                  }}
                >
                  <Icon name={stats.delta >= 0 ? 'TrendingUp' : 'TrendingDown'} size={15} />
                  {stats.delta >= 0 ? '+' : ''}
                  {stats.delta}
                </span>
              ) : null}
              {percentile ? (
                <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 2 }}>{percentile}</div>
              ) : null}
            </div>
          ) : null}
        </div>
      </Card>

      {/* ---- chart ---- */}
      <Card padding="md" style={{ marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-3)',
            marginBottom: 8,
          }}
        >
          <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--text-md)', margin: 0 }}>
            パーセンタイル曲線
          </h3>
          <span style={{ fontSize: 10, color: 'var(--text-subtle)', textAlign: 'right' }}>
            WHO 3–97% · Percentile
          </span>
        </div>

        {ofKind.length ? (
          <GrowthChart kind={kind} sex={child.sex} birthDate={child.birthDate} entries={ofKind} />
        ) : (
          <EmptyState>
            記録を追加すると、WHOの成長曲線とくらべて表示されます。
          </EmptyState>
        )}

        {child.sex === 'unspecified' && ofKind.length ? (
          <div style={{ fontSize: 10, color: 'var(--text-subtle)', marginTop: 8, textAlign: 'center' }}>
            性別が未設定のため女の子の曲線を表示しています。設定から変更できます。
          </div>
        ) : null}
      </Card>

      <Button variant="outline" fullWidth iconStart="Plus" onClick={() => setAdding(true)}>
        {kind === 'weight' ? '体重を記録' : '身長を記録'}
      </Button>

      {/* ---- log ---- */}
      <div style={{ marginTop: 20 }}>
        <MutedHeading>記録 · Log</MutedHeading>
        <Card padding="none" style={{ overflow: 'hidden', marginBottom: 20 }}>
          {ofKind.length ? (
            ofKind.map((entry, i) => (
              <div
                key={entry.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--space-3)',
                  padding: '12px 15px',
                  borderBottom: i === ofKind.length - 1 ? 'none' : '1px solid var(--border)',
                }}
              >
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', minWidth: 0 }}>
                  <Mono>{formatMonthDay(entry.measuredAt)}</Mono>
                  <span style={{ fontSize: 11, color: 'var(--text-subtle)', marginLeft: 6 }}>
                    {ageLabel(child.birthDate, entry.measuredAt)}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none' }}>
                  <Mono style={{ fontSize: 'var(--text-md)' }}>
                    {entry.value} {unit}
                  </Mono>
                  <IconButton
                    icon="Trash2"
                    label="この記録を削除"
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      if (!familyId) return;
                      await api.deleteGrowth(familyId, entry.id);
                      toast.show({ title: '削除しました' });
                    }}
                  />
                </div>
              </div>
            ))
          ) : (
            <EmptyState>まだ記録がありません。</EmptyState>
          )}
        </Card>
      </div>

      {/* ---- milestones ---- */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
          marginBottom: 8,
        }}
      >
        <h3
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            fontSize: 'var(--text-md)',
            margin: 0,
            color: 'var(--text-muted)',
          }}
        >
          はじめて · Milestones
        </h3>
        <Switch
          checked={showMilestones}
          onChange={(next) => familyId && void api.setShowMilestones(familyId, next)}
          size="sm"
          aria-label="はじめてを表示"
        />
      </div>

      {showMilestones ? (
        <>
          {childMilestones.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {childMilestones.map((m) => (
                <MilestoneRow
                  key={m.id}
                  milestone={m}
                  onDelete={async () => {
                    if (!familyId) return;
                    await api.deleteMilestone(familyId, m.id);
                  }}
                />
              ))}
            </div>
          ) : (
            <Card padding="md">
              <EmptyState icon={<Icon name="Smile" size={22} />}>
                はじめての笑顔、寝返り…気づいたときに残しておけます。
              </EmptyState>
            </Card>
          )}

          <Button
            variant="ghost"
            fullWidth
            iconStart="Plus"
            style={{ marginTop: 10 }}
            onClick={() => setAddingMilestone(true)}
          >
            はじめてを追加
          </Button>
        </>
      ) : null}

      {/* ---- sheets ---- */}
      <AddMeasurementSheet
        open={adding}
        kind={kind}
        unit={unit}
        onClose={() => setAdding(false)}
        onSave={async (value, measuredAt) => {
          if (!familyId || !me) return;
          await api.addGrowth(familyId, me, { childId: child.id, kind, value, measuredAt });
          toast.showSaved(`${value} ${unit}`);
          setAdding(false);
        }}
      />

      <AddMilestoneSheet
        open={addingMilestone}
        onClose={() => setAddingMilestone(false)}
        onSave={async (draft) => {
          if (!familyId || !me) return;
          await api.addMilestone(familyId, me, { ...draft, childId: child.id });
          toast.showSaved(draft.jp);
          setAddingMilestone(false);
        }}
      />
    </Screen>
  );
}

function MilestoneRow({ milestone, onDelete }: { milestone: Milestone; onDelete: () => Promise<void> }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        padding: '11px 13px',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--surface-sunken)',
        border: '1px solid var(--border)',
      }}
    >
      <span
        style={{
          flex: 'none',
          width: 30,
          height: 30,
          borderRadius: '50%',
          background: 'var(--sky-100)',
          color: 'var(--sky-600)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name={milestone.icon} size={15} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text)' }}>{milestone.jp}</div>
        <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>{milestone.en}</div>
      </div>
      <Mono style={{ fontSize: 'var(--text-xs)', color: 'var(--text-subtle)' }}>
        {milestone.date ? formatMonthDay(parseISODate(milestone.date)) : ''}
      </Mono>
      <IconButton icon="Trash2" label="削除" variant="ghost" size="sm" onClick={() => void onDelete()} />
    </div>
  );
}

function AddMeasurementSheet({
  open,
  kind,
  unit,
  onClose,
  onSave,
}: {
  open: boolean;
  kind: GrowthKind;
  unit: string;
  onClose: () => void;
  onSave: (value: number, measuredAt: Date) => Promise<void>;
}) {
  const [text, setText] = useState('');
  const [date, setDate] = useState(() => toISODate(new Date()));
  const [saving, setSaving] = useState(false);

  const value = Number.parseFloat(text.replace(',', '.'));
  const valid = Number.isFinite(value) && value > 0;

  return (
    <Sheet
      open={open}
      onClose={() => {
        setText('');
        onClose();
      }}
      eyebrow={kind === 'weight' ? 'Add a weight' : 'Add a height'}
      title={kind === 'weight' ? '体重を記録' : '身長を記録'}
      footer={
        <Button
          variant="primary"
          fullWidth
          size="lg"
          iconStart="Check"
          disabled={!valid || saving}
          onClick={async () => {
            setSaving(true);
            try {
              await onSave(value, parseISODate(date));
              setText('');
            } finally {
              setSaving(false);
            }
          }}
        >
          {saving ? '保存中…' : '保存する · Save'}
        </Button>
      }
    >
      <Field label={kind === 'weight' ? `体重 · Weight (${unit})` : `身長 · Height (${unit})`}>
        <TextInput
          value={text}
          onChange={setText}
          placeholder={kind === 'weight' ? '6.4' : '63.5'}
          inputMode="decimal"
          autoFocus
        />
      </Field>

      <SheetRow first label={<Eyebrow>はかった日 · Date</Eyebrow>}>
        <input
          type="date"
          value={date}
          max={toISODate(new Date())}
          onChange={(e) => setDate(e.target.value)}
          aria-label="はかった日"
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--text-sm)',
            color: 'var(--text)',
            background: 'transparent',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-xs)',
            padding: '6px 8px',
          }}
        />
      </SheetRow>
    </Sheet>
  );
}

function AddMilestoneSheet({
  open,
  onClose,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (draft: Omit<Milestone, 'id' | 'childId'>) => Promise<void>;
}) {
  const [jp, setJp] = useState('');
  const [en, setEn] = useState('');
  const [icon, setIcon] = useState('Smile');
  const [date, setDate] = useState(() => toISODate(new Date()));
  const [saving, setSaving] = useState(false);

  return (
    <Sheet
      open={open}
      onClose={onClose}
      eyebrow="Add a first"
      title="はじめてを追加"
      footer={
        <Button
          variant="primary"
          fullWidth
          size="lg"
          iconStart="Check"
          disabled={!jp.trim() || saving}
          onClick={async () => {
            setSaving(true);
            try {
              await onSave({ jp: jp.trim(), en: en.trim(), icon, date });
              setJp('');
              setEn('');
            } finally {
              setSaving(false);
            }
          }}
        >
          {saving ? '保存中…' : '保存する · Save'}
        </Button>
      }
    >
      <Eyebrow style={{ marginBottom: 9 }}>よくあるもの · Suggestions</Eyebrow>
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 18 }}>
        {MILESTONE_SUGGESTIONS.map((s) => (
          <Tag
            key={s.jp}
            icon={s.icon}
            selected={jp === s.jp}
            onClick={() => {
              setJp(s.jp);
              setEn(s.en);
              setIcon(s.icon);
            }}
          >
            {s.jp}
          </Tag>
        ))}
      </div>

      <Field label="できたこと · Milestone">
        <TextInput value={jp} onChange={setJp} placeholder="はじめての笑顔" />
      </Field>
      <Field label="English">
        <TextInput value={en} onChange={setEn} placeholder="First smile" />
      </Field>

      <SheetRow first label={<Eyebrow>日付 · Date</Eyebrow>}>
        <input
          type="date"
          value={date}
          max={toISODate(new Date())}
          onChange={(e) => setDate(e.target.value)}
          aria-label="日付"
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--text-sm)',
            color: 'var(--text)',
            background: 'transparent',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-xs)',
            padding: '6px 8px',
          }}
        />
      </SheetRow>
    </Sheet>
  );
}
